import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { LookupItem } from '../../types/global';

type RequestStatuses = {
  payload: LookupItem[];
};

/** Document item from getSingleRequest payload.documents */
export type SingleRequestDocument = {
  id: string;
  profileId: string;
  userId: string;
  type: string;
  name: string;
  blobName: string;
  blobFileUrl: string;
  blobSize: number;
};

/** getSingleRequest payload.documents */
export type SingleRequestDocuments = {
  commercialRegister: SingleRequestDocument[];
  nationalId: SingleRequestDocument[];
  delegation: SingleRequestDocument[];
  taxNumber: SingleRequestDocument[];
};

/** getSingleRequest payload.address */
export type SingleRequestAddress = {
  countryId: string;
  countryCode: string | null;
  countryName: string | null;
  stateId: string | null;
  stateCode: string | null;
  stateName: string | null;
  street: string;
  isHeadquarter: boolean;
};

/** getSingleRequest payload.company */
export type SingleRequestCompany = {
  name: string;
  commercialRegister: string;
  taxNumber: string;
  companyTypeId?: string | null;
  companyTypeName: string | null;
};

/** getSingleRequest payload.applicant */
export type SingleRequestApplicant = {
  firstName: string;
  lastName: string;
  email: string;
  phoneCode: string | null;
  phoneNumber: string | null;
  applicantType: string;
  dob: string | null;
  userName: string | null;
};

/** getSingleRequest payload.status */
export type SingleRequestStatus = {
  id: string;
  code: string;
  name: string;
};

/** getSingleRequest response.payload */
export type SingleRequestPayload = {
  id: string;
  requestId: string;
  profileId: string;
  userId: string;
  createdDate: string;
  status: SingleRequestStatus;
  applicant: SingleRequestApplicant;
  company: SingleRequestCompany;
  address: SingleRequestAddress;
  documents: SingleRequestDocuments;
  editedFields: string[];
};

/** getSingleRequest full response */
export type SingleRequestResponse = {
  payload: SingleRequestPayload;
};

/** getOperationActions response.payload */
export type OperationActionsPayload = {
  canApprove: boolean;
  canEdit: boolean;
  canReject: boolean;
  canReturn: boolean;
};

/** getOperationActions full response */
export type OperationActionsResponse = {
  payload: OperationActionsPayload;
};

/** getBusinessStatistics response payload */
export type BusinessStatistics = {
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  waitingAcceptanceRequests: number;
};

/** getBusinessStatistics full response */
export type BusinessStatisticsResponse = {
  payload: BusinessStatistics;
};

export const businessRequestsApi = createApi({
  reducerPath: 'businessRequestsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BusinessActions', 'BusinessList'],
  endpoints: ({ query, mutation }) => ({
    getBusinessStatistics: query<BusinessStatisticsResponse, void>({
      query: () => '/profile-service/v1/operator/business/statistics',
    }),
    getBusinessList: query<any, Record<string, string | number | undefined>>({
      query: (params) => ({
        url: '/profile-service/v1/operator/business',
        params,
      }),
      providesTags: ['BusinessList'],
    }),
    getSingleRequest: query<SingleRequestResponse, { requestId: string }>({
      query: ({ requestId }) => ({
        url: `/profile-service/v1/operator/business/${requestId}`,
      }),
    }),
    getStatusLookups: query<RequestStatuses, void>({
      query: () => ({
        url: '/profile-service/v1/operator/business-status',
      }),
    }),
    getOperationActions: query<OperationActionsResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/profile-service/v1/operator/business/${id}/actions`,
      }),
      providesTags: ['BusinessActions'],
    }),
    editBusinessInfo: mutation({
      query: ({ id, ...payload }: { id: string; [key: string]: unknown }) => ({
        url: `/profile-service/v1/operator/business/${id}/edit`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['BusinessActions', 'BusinessList'],
    }),
    returnBusinessInfo: mutation({
      query: (body: { returnReason: string; id: string }) => ({
        url: `/profile-service/v1/operator/business/${body.id}/return`,
        method: 'POST',
        body: { returnReason: body.returnReason },
      }),
      invalidatesTags: ['BusinessActions', 'BusinessList'],
    }),
    approveUserOperation: mutation({
      query: ({
        body,
        id,
      }: {
        body: { email: string };
        id: string | null;
      }) => ({
        url: `/profile-service/v1/operator/business/${id}/approve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BusinessActions', 'BusinessList'],
    }),
    rejectUserOperation: mutation({
      query: ({
        body,
        id,
      }: {
        body: { rejectReason: string; email: string };
        id: string | null;
      }) => ({
        url: `/profile-service/v1/operator/business/${id}/reject`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BusinessActions', 'BusinessList'],
    }),
  }),
});

export const {
  useGetBusinessStatisticsQuery,
  useGetBusinessListQuery,
  useGetSingleRequestQuery,
  useGetStatusLookupsQuery,
  useGetOperationActionsQuery,
  useEditBusinessInfoMutation,
  useReturnBusinessInfoMutation,
  useApproveUserOperationMutation,
  useRejectUserOperationMutation,
} = businessRequestsApi;
